import { useState, useEffect, Dispatch } from 'react'
import sortBy from 'lodash.sortby'

import { getPreDataJSON } from '@pepper/utils/src/api/utils/utils'
import { createProject as createProjectDraft } from '@pepper/utils/src/api/projects/projects'

import useUserAccountType from 'utils/hooks/useUserAccountType'

export type VerticalOptions = {
  isStandard?: boolean
  isRestricted?: boolean
  key: string
  id: number
  title: string
  description?: string
  icon_set?: {
    blue: string
    gray: string
    default: string
  }
}

export enum Mode {
  ADD_ASSIGNMENT,
  CREATE_PROJECT,
}

export const navigationLinks = ['Popular Orders', 'Recent Orders']

export const onAssignmentMode = (
  response: PreDataProps,
  title: string
): PreDataProps => {
  return {
    ...response,
    typeContentSelection: {
      ...response.typeContentSelection,
      header: title,
    },
  }
}

export const formatSubcategories = (
  subCategories: SubCategories[],
  accountType: ClientAccountType | null,
  preDataSet: PreDataProps | null
): SubCategories[] => {
  let result: SubCategories[] = []
  if (accountType === 'standard') {
    result = subCategories.filter(item => item.title !== 'Others')
  } else {
    result = subCategories
  }

  // Filter Restricted Subcategories
  const filteredSubCategories = preDataSet?.typeContentSelection.body.options
    .filter(item => item.options.length > 0)
    .map(item => item.id)

  result = result.filter(item => {
    return filteredSubCategories?.some(category => {
      return category === item.id
    })
  })

  // Move Other SubCategory to bottom
  const [other] = result.filter(item => item.title === 'Others')
  result = result.filter(item => item.title !== 'Others')
  result = sortBy(result, ['title'])
  result = other ? [...result, other] : result
  return result
}

export const formatPreData = (
  preDataset: PreDataProps | null,
  accountType: ClientAccountType | null,
  company?: Company
): PreDataProps | null => {
  if (preDataset !== null) {
    // Remove Others Category if the account type is Standard
    if (accountType === 'standard') {
      preDataset.typeContentSelection.body.options =
        preDataset.typeContentSelection.body.options
          .filter(item => !!item)
          .filter(item => item.name !== 'Others')
    }

    // Remove restrict flag verticals
    if (company && company.all_verticals_access_flag === false) {
      const result = preDataset.typeContentSelection.body.options.map(item => {
        const verticals = item.options.filter(vertical => vertical.isRestricted)
        return { ...item, options: verticals }
      })

      preDataset.typeContentSelection.body.options = result
    }

    preDataset.typeContentSelection.body.options = sortBy(
      preDataset?.typeContentSelection.body.options,
      'name'
    )

    // Logic to move other subcategory to the bottom of the array
    const [other] = preDataset.typeContentSelection.body.options.filter(
      item => item.name === 'Others'
    )
    const options = preDataset.typeContentSelection.body.options.filter(
      item => item.name !== 'Others'
    )
    preDataset.typeContentSelection.body.options = other
      ? [...options, other]
      : options
  }

  return preDataset
}

export const getVerticalOptions = (
  options: VerticalID[],
  preDataset: PreDataProps
): VerticalOptions[] => {
  if (!options) return []
  const ids = options.map(item => item.vertical_id)
  return preDataset.typeContentSelection.body.options.flatMap(subCategory => {
    return subCategory.options.filter(verticals => {
      return ids.includes(verticals.id)
    })
  })
}

export const usePrefetchData = (
  mode: Mode,
  title: string
): [PreDataProps | null, Dispatch<PreDataProps>] => {
  const [preDataset, setPreDataset] = useState<PreDataProps | null>(null)
  const accountType = useUserAccountType()

  useEffect(() => {
    const fetchCreateProject = async () => {
      if (typeof accountType !== 'string') return

      const isStandard = accountType === 'standard'

      const res = await getPreDataJSON({ isStandard })
      if (res.data) {
        let response = res.data

        if (mode === Mode.ADD_ASSIGNMENT) {
          response = onAssignmentMode(response, title)
        }

        setPreDataset(response)
      }
    }

    fetchCreateProject()
  }, [accountType, mode, title])

  return [preDataset, setPreDataset]
}

export const handleCreateProject = async (
  companyId: string,
  categoryId: string,
  verticalId: number | null,
  projectId?: string,
  body?: Record<string, unknown>
) => {
  const { status, error, data } = await createProjectDraft({
    body: {
      company_id: companyId,
      project_category: categoryId,
      vertical_id: verticalId,
      project_id: projectId,
      ...body,
    },
    type: 'draft',
  })

  return {
    status,
    data,
    error,
  }
}
